import { Paper } from '@mui/material';
import { Row, RowSelectionState } from '@tanstack/react-table';
import { TerminalContainer } from 'modules/global/adapters/crpAPI';
import StepUseCase from 'modules/global/components/Stepper/StepUseCase';
import { usePostContainersDecline } from 'modules/transport-operator/adapters/decline-releaseright/queries';
import { maxNumberOfContainersProcessed } from 'modules/transport-operator/constants';
import { FAQ_PAGES } from 'modules/release-party/constants';
import React from 'react';
import { useTranslation } from 'react-i18next';
import FilterStep from './FilterStep';
import ResultStep from '../shared/ResultStep';
import UpdateStep from './UpdateStep';
import { Permission } from 'auth/permissions';

type Props = {
  data: TerminalContainer[] | undefined;
  selectedRows: Row<TerminalContainer>[] | null;
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  setDisplayGrid: React.Dispatch<React.SetStateAction<boolean>>;
};

const StepperDeclineReleaseRight = ({
  data,
  selectedRows,
  setRowSelection,
  setDisplayGrid,
}: Props) => {
  const { t } = useTranslation(['declineReleaseRight', 'common', 'useCase', 'validation']);
  const [activeStep, setActiveStep] = React.useState(0);
  const [containersUpdated, setContainersUpdated] = React.useState(0);
  const [newReason, setReason] = React.useState('');

  const listOfCheckedContainers = data && selectedRows?.map((row) => row.original.releaseId);
  const numberOfContainersSelected = selectedRows?.length || 0;
  const numberOfContainersProcessed =
    numberOfContainersSelected > maxNumberOfContainersProcessed
      ? maxNumberOfContainersProcessed
      : numberOfContainersSelected;

  const resetFormAndContainersUpdated = () => {
    setRowSelection({});
    setContainersUpdated(0);
    setReason('');
  };

  const { mutate, status } = usePostContainersDecline();

  const declineReleaseRight = () => {
    setContainersUpdated(numberOfContainersProcessed);
    mutate({
      releaseIds: listOfCheckedContainers,
      reason: newReason,
    });
  };

  return (
    <Paper sx={{ p: 2, minWidth: 0, overflow: 'visible' }}>
      <StepUseCase
        faqLink={FAQ_PAGES.DECLINE_RELEASE_RIGHT}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        steps={[
          {
            stepTitle: t('useCase:containers.selectContainers'),
            stepFlavorText: t('useCase:containers.selectPendingContainersFlavorText'),
            nextCustomText: t('common:general.next'),
            disableNext: numberOfContainersSelected === 0,
            requiredPermission: Permission.CAN_EDIT,
            backCallBack: () => setDisplayGrid(false),
            content: (
              <FilterStep
                numberOfContainersSelected={numberOfContainersSelected}
                numberOfContainers={data?.length || 0}
              />
            ),
          },
          {
            stepTitle: t('declineReleaseRight:general.title'),
            stepFlavorText: t('useCase:containers.declineContainersFlavorText'),
            nextCallBack: declineReleaseRight,
            nextCustomText: t('useCase:containers.declineContainers', {
              numberOfContainersSelected: numberOfContainersProcessed,
            }),
            disableNext: numberOfContainersSelected < 1,
            content: (
              <UpdateStep
                numberOfContainersSelected={numberOfContainersSelected}
                setReason={setReason}
                newReason={newReason}
              />
            ),
          },
          {
            stepTitle: t('common:general.result'),
            noStepButtons: true,
            content: (
              <ResultStep
                status={status}
                numberOfContainersUpdated={containersUpdated}
                setActiveStep={setActiveStep}
                retryFunction={declineReleaseRight}
                customReturnToSearchText="common:general.backToSelection"
                callBackOnReturnToSearch={resetFormAndContainersUpdated}
                resultText={t('sideBar.containersUpdated', {
                  numberOfContainersUpdated: containersUpdated,
                })}
                hasNextStep={false}
              />
            ),
          },
        ]}
      />
    </Paper>
  );
};

export default StepperDeclineReleaseRight;
